import AssetGroup, {
  AssetDescription,
} from '@play-co/timestep-core/lib/ui/resource/AssetGroup';
import { LoadMethod } from '@play-co/timestep-core/lib/ui/resource/loader';
import { createQueryablePromiseFromPromise } from '@play-co/timestep-core/lib/ui/utils/QueryablePromise';
import type { FontMetadataSchema } from '@play-co/timestep-core/lib/ui/bitmapFont/fontRenderer/types';
import {
  getFontFamily,
  loadFont,
} from '@play-co/timestep-core/lib/ui/bitmapFont/fontRenderer/utils';
import type { OptimisticResource } from './optimisticLoading';
import {
  BitmapFontFile,
  getBitmapFontDataPaths,
  getBitmapFontFiles,
} from './lib/bitmapFonts';
import {
  getBrowserLocale,
  getLocalePath,
  getUsableLocale,
} from './lib/i18n/i18n';
import { readLocaleHint } from './lib/i18n/localeHint';
import { isLoadingFTUEActive } from './lib/ftue';
// Circular with `loadMatch2LevelAssets` (it imports `getBlockAssetPath`);
// both sides only use the other inside functions, never at module scope.
import { getFirstPuzzleWarmUpAssets } from './loadMatch2LevelAssets';
import { getDisplayedPetState } from './game/pet/pet.util';
import { MovieClip } from '@play-co/timestep-core/ui';
import { PetSpeciesID } from './replicant/pet';
import * as Pet from './replicant/pet';
import { Locale } from './replicant/ruleset/locale';
import { TutorialStep } from './replicant/tutorial/tutorial.ruleset';
import uiConfig from './lib/ui/config';
import { petAnimations } from './replicant/pet/PetAssets';
import { CareItemType } from './replicant/care/care.ruleset';
import {
  AnimationName,
  getStatusAnimationName,
} from './game/appearance/appearance.config';
import { exhaustiveTuple } from './replicant/utils/types';
import {
  BlockId,
  blockPropsMap,
} from './game/minigame/match2-timestep/game/defs/block';
import { blockViewAssets } from './game/minigame/match2-timestep/game/util/blockTools';
import StateObserver, { user } from './StateObserver';
import features from './replicant/ruleset/features';
import {
  getPetConversationAssetPaths,
  NEW_PET_DIALOG_SPECIES,
} from './game/conversation/conversation.assets';

/**
 * Defers building an `AssetGroup` until it is first loaded.
 *
 * `AssetGroup.constructURLs` scans the whole sprite map once per path, so
 * building groups at module scope moves that cost into module evaluation,
 * where it blocks the main thread before any asset can be requested.
 */
function lazyAssetGroup(create: () => AssetGroup): Pick<AssetGroup, 'load'> {
  let group: AssetGroup | undefined;

  return {
    load: (contextualAssetsURLs?: string[]) =>
      (group ??= create()).load(contextualAssetsURLs),
  };
}

// TODO: Ideally use `AssetGroup.PRIORITY_HIGH`.
// timestep-core loader has not yet implemented high priority.
export const initialAssets = lazyAssetGroup(
  () =>
    new AssetGroup(
      [
        ...AssetGroup.constructURLs([
          // These items should match the assets-init in assets/metadata.json
          'assets/ui/hud/icons',
          'assets/ui/hud/meters',
          'assets/ui/hud/frames',
          'assets/ui/menu',
          'assets/ui/shared/buttons',
          'assets/ui/shared/frames',
          'assets/ui/shared/icons',
          'assets/ui/conversation',
          'assets/ui/popups',
          'assets/ui/badge',
          'assets/tutorial/hand/tutorial_circle.png',
          'assets/tutorial/hand/tutorial_hand.png',

          // Friends menu icons
          'assets/ui/friends/team_cafe.png',
          'assets/ui/friends/friend_action_referral.png',
          'assets/ui/friends/friend_action_gift.png',
          'assets/ui/friends/friend_action_visitorslist.png',
          'assets/ui/friends/friend_action_visitfriend.png',
          'assets/ui/duo/icons/duo_received.png',
          'assets/ui/duo/icons/duo_sent.png',
          'assets/ui/friends/icon_friend_requests.png',
          'assets/ui/friends/icon_add_friends.png',

          // Care menu icons
          'assets/ui/care/care_action_food.png',
          'assets/ui/care/care_action_snack.png',
          'assets/ui/care/care_action_toy.png',
          'assets/ui/care/care_action_firstaid.png',
          'assets/ui/care/care_action_bath.png',
          'assets/ui/care/care_action_toilet.png',
          'assets/ui/growthacceleration/action_growthcapsule_icon.png',
          'assets/ui/match2/common/icon_infinite_green.png',
          'assets/ui/match2/goals/icon.rocket.horizontal.png',

          // Assets to build the initial background
          `assets/ui/decoration/frames/living_room_header.png`,
          'assets/ui/decoration/background/living_room_base.png',
          'assets/decoration/01_living_room/default/01_wallpaper.png',
          'assets/decoration/01_living_room/default/02_floor.png',
        ]),
      ],
      undefined,
      AssetGroup.PRIORITY_MEDIUM,
    ),
);

export const careAssets: Record<
  Exclude<CareItemType, 'invitation'>,
  AssetDescription<any>
> = {
  first_aid: {
    url: `assets/care/firstAid`,
    loadMethod: MovieClip.animationLoader,
  },
  food: {
    url: `assets/care/food`,
    loadMethod: MovieClip.animationLoader,
  },
  snack: {
    url: `assets/care/snack`,
    loadMethod: MovieClip.animationLoader,
  },
  toy: {
    url: `assets/care/toy`,
    loadMethod: MovieClip.animationLoader,
  },
};

// Assets to be loaded in the background.
export const getBackgroundAssets = () => {
  return new AssetGroup(
    [
      // List UI assets that should be loaded in background
      ...AssetGroup.constructURLs([
        // Most important sounds
        `assets/sounds/sfx/Cancel_Back.mp3`,
        `assets/sounds/sfx/OK.mp3`,
        `assets/sounds/sfx/Speak-0.mp3`,
        `assets/sounds/sfx/Speak-1.mp3`,
        `assets/sounds/sfx/Speak-2.mp3`,
        `assets/sounds/sfx/TapPet.mp3`,

        // UI and popup sounds (#9977)
        `assets/sounds/sfx/Applause.mp3`,
        `assets/sounds/sfx/progress_counted.mp3`,
        `assets/sounds/sfx/GachaItemDrop.mp3`,
        `assets/sounds/sfx/Move.mp3`,
        `assets/sounds/sfx/win.mp3`,
        `assets/sounds/sfx/tada.mp3`,
        `assets/sounds/sfx/NormalHit.mp3`,
        `assets/sounds/sfx/button-power.mp3`,

        // General UI assets
        `assets/ui/care`,
        `assets/ui/friends`, // This is here only because of the menu icons

        // Room assets
        `assets/ui/bathroom`,
        `assets/ui/bedroom`,
        `assets/ui/diningroom`,
        `assets/ui/toilet`,

        // Puzzle assets
        `assets/ui/match2/footer`,
        `assets/ui/match2/frenzy/common`,
        `assets/ui/match2/goals`,
        `assets/ui/match2/level`,
        `assets/ui/match2/movepinch`,
        `assets/ui/match2/common/`,

        // Shop assets
        `assets/ui/match2/shopbundles`,
        `assets/ui/shop`,
      ]),

      // Load default toilet room assets.
      ...AssetGroup.constructURLs([
        'assets/decoration/04_toilet_room/default/04_toilet.png',
        'assets/decoration/04_toilet_room/default/05_toilet_ornament.png',
        'assets/decoration/04_toilet_room/default/03_arch.png',
      ]),
      // Load all care animations.
      ...Object.values(careAssets),
    ],
    undefined,
    AssetGroup.PRIORITY_LOW,
  );
};

export const petEvolutionAssets = new AssetGroup([
  {
    url: `assets/care/evolve`,
    loadMethod: MovieClip.animationLoader,
  },
]);

const petSpeciesIDs: ReadonlySet<PetSpeciesID> = new Set(Pet.ruleset.species);

const expandedDialogSpecies: ReadonlySet<PetSpeciesID> = new Set(
  NEW_PET_DIALOG_SPECIES,
);

/**
 * One group per species and dialog version. Species keep a stable group per
 * version so callers that read `assetsByID` after `load()` see the same
 * instance the assets were loaded into.
 */
const petSpeciesAssetGroups = new Map<string, AssetGroup>();

function createPetSpeciesAssets(
  species: PetSpeciesID,
  useExpandedDialogs: boolean,
): AssetGroup {
  const { dialogs, scripts } = getPetConversationAssetPaths(
    species,
    useExpandedDialogs,
  );

  return new AssetGroup([
    ...AssetGroup.constructURLs([
      `assets/species/${species}/icon.png`,
      dialogs,
      scripts,
    ]),
    {
      url: petAnimations[species].url,
      loadMethod: MovieClip.animationLoader,
    },
  ]);
}

/**
 * Species asset groups are built on first access rather than at module scope:
 * only the player's own species is normally needed, and each group costs a
 * full sprite map scan per path to construct. The proxy target stays empty;
 * groups live in `petSpeciesAssetGroups`.
 */
const petSpeciesAssetsTarget = {} as Record<PetSpeciesID, AssetGroup>;

/**
 * Selects the asset group for the user's A/B bucket, which is only known once
 * state is available. The proxy preserves the existing
 * `petSpeciesAssets[species]` API.
 */
export const petSpeciesAssets = new Proxy(petSpeciesAssetsTarget, {
  get(target, property): AssetGroup {
    const species = property as PetSpeciesID;

    // Unknown keys resolve to `undefined`, as they did when this was a plain
    // record of every species.
    if (!petSpeciesIDs.has(species)) return target[species];

    // Species without expanded dialogs resolve to the same assets in both
    // buckets, so they share a single group rather than one per bucket.
    const useExpandedDialogs =
      features(user()).newPetDialogs && expandedDialogSpecies.has(species);
    const cacheKey = `${species}:${useExpandedDialogs ? 'v2' : 'v1'}`;

    let group = petSpeciesAssetGroups.get(cacheKey);
    if (!group) {
      group = createPetSpeciesAssets(species, useExpandedDialogs);
      petSpeciesAssetGroups.set(cacheKey, group);
    }

    return group;
  },
});

const statusAnimations = exhaustiveTuple<AnimationName>()(
  'sick',
  'hungry',
  'dirty',
  'tooth_decay',
  'scared',
  'lonely',
  'pee',
  'want_out',
  'happy',
);

type StatusAnimationsAssets = Record<
  (typeof statusAnimations)[number],
  AssetGroup
>;

function getStatusAnimationPath(status: AnimationName, locale: Locale) {
  return `assets/status/state_${status}_${locale}`;
}

export const petStatusAnimationsEN = statusAnimations.reduce(
  (acc, status) => ({
    ...acc,
    [status]: new AssetGroup([
      {
        url: getStatusAnimationPath(status, 'en'),
        loadMethod: MovieClip.animationLoader,
      },
    ]),
  }),
  {} as StatusAnimationsAssets,
);

export const petStatusAnimationsJP = statusAnimations.reduce(
  (acc, status) => ({
    ...acc,
    [status]: new AssetGroup([
      {
        url: getStatusAnimationPath(status, 'ja'),
        loadMethod: MovieClip.animationLoader,
      },
    ]),
  }),
  {} as StatusAnimationsAssets,
);

/**
 * A movieclip and the spritesheets it will ask for. A clip is a two hop load:
 * its `data.json` names the sheets, so `animationLoader` cannot request one
 * until that round trip has parsed. The sprite map already knows which sheets
 * the directory resolves to, so they are requested alongside the JSON and the
 * clip's own second hop finds them in the cache.
 */
function getAnimationAssets(
  url: string,
): (string | AssetDescription<unknown>)[] {
  return [
    { url, loadMethod: MovieClip.animationLoader },
    ...AssetGroup.constructURLs(url),
  ];
}

export const petCrateAssets = new AssetGroup([
  {
    url: `assets/tutorial/crate`,
    loadMethod: MovieClip.animationLoader,
  },
  `assets/ui/shared/frames/popup_overlay.png`,
]);

export const petGraduationAssets = new AssetGroup([
  `assets/progress/graduation_door.png`,
]);

export const petTutorialAssets = [
  ...Object.entries(uiConfig.buruburu.tutorial.images),
].reduce(
  (acc, [step, { en, ja }]) => ({
    ...acc,
    [step]: {
      en: new AssetGroup(en),
      ja: new AssetGroup(ja),
    },
  }),
  {} as Record<TutorialStep, Record<Locale, AssetGroup>>,
);

export const accessionAssets = new AssetGroup([
  'assets/sounds/sfx/Ascension.mp3',
]);

export const duoMugAssets = new AssetGroup([
  {
    url: 'assets/care/invitation',
    loadMethod: MovieClip.animationLoader,
  },
]);

// Match2
export function getBlockAssetPath(
  blocks: BlockId[] = Object.keys(blockPropsMap) as BlockId[],
) {
  const blockAssetPaths: string[] = [];
  const blockAssetDescriptions: AssetDescription<any>[] = [];
  blocks
    .map((blockId) => blockViewAssets(blockId as BlockId))
    .flat()
    .forEach((asset) => {
      if (typeof asset === 'string') {
        // Do not push empty paths.
        if (asset !== 'assets/minigame/match2/puzzle/') {
          blockAssetPaths.push(asset);
        }
        return;
      }

      blockAssetDescriptions.push(asset);
    });
  return { blockAssetPaths, blockAssetDescriptions };
}

export const magicLampsAssets = lazyAssetGroup(
  () =>
    new AssetGroup([
      ...AssetGroup.constructURLs([`assets/ui/match2/magiclamps/`]),
    ]),
);

/**
 * Parses a font file into `document.fonts` under the family its variants
 * share, ahead of the renderer's own request for it.
 *
 * `TTFFontRenderer` loads a file with timestep-core's `loadFont`, naming the
 * `FontFace` after the family in the variant's metadata, and constructing the
 * face is the expensive part: about a second of main thread for the mplus
 * file at 13x CPU throttling, all of it after login when the renderer is the
 * first to ask. Calling `loadFont` here with the same path and family moves
 * that parse to before login, while the main thread is otherwise idle. It
 * warms the browser-side caches of parsed faces: `document.fonts`, and
 * timestep-core's memo of `loadFont` results per family and file, which turns
 * the renderer's own call into a hit. The metadata goes through the loader so
 * that the `loadJSONs` in `initializeBitmapFonts` for the same path is a hit
 * too.
 *
 * Nothing is installed: `initializeBitmapFonts` still creates every
 * `TTFBitmapFont` and remains the only writer to `fontCache`. Every variant of
 * a file shares its family, so one variant's metadata names the face for the
 * whole file. A wrong locale guess costs one parse on an idle main thread and
 * an unused face in `document.fonts`. `paytone_number` exists under that
 * family in both locales from different files, so a wrong guess adds a second
 * face with that family; the two files are built from the same source font
 * and character set, so the faces render alike. A failed warm-up resolves
 * rather than rejects, like every other loader request: the renderer's load
 * issues the same requests and surfaces its own failure. The loader keeps no
 * result, so this resolves to nothing.
 */
function parseFontFile(dataPath: string): LoadMethod<undefined> {
  return (fontPath, fileLoader) =>
    createQueryablePromiseFromPromise(
      (async () => {
        const metadata = (await fileLoader.loadJSON(dataPath)) as unknown as
          | FontMetadataSchema
          | undefined;
        if (metadata) await loadFont(fontPath, getFontFamily(metadata));
      })().then(
        () => undefined,
        () => undefined,
      ),
    );
}

/** A font file, at the path the renderer will ask for it by. */
function getFontFileAsset({
  fontPath,
  dataPath,
}: BitmapFontFile): AssetDescription<undefined> {
  return { url: fontPath, loadMethod: parseFontFile(dataPath) };
}

/**
 * Assets to warm up before the code that needs them asks, keyed to the launch
 * phase at which enough is known to name them. `OptimisticResource` states the
 * rules a resource has to follow.
 */
export const optimisticResources: OptimisticResource[] = [
  {
    // The strings, font metadata and parsed font faces for the locale this
    // device most likely wants. The authoritative locale is only known after
    // the Replicant handshake, and loading these behind it puts the round
    // trips, the font download and the font parse on the critical path. The
    // hint from the previous launch is right unless the player changed
    // language elsewhere; the browser language is the fallback.
    name: 'locale',
    phase: 'launch',
    assets: () => {
      const locale = getUsableLocale(readLocaleHint() ?? getBrowserLocale());
      return new AssetGroup([
        getLocalePath(locale),
        ...getBitmapFontDataPaths(locale),
        ...getBitmapFontFiles(locale).map(getFontFileAsset),
      ]);
    },
  },
  {
    // The pet's own clip and the clip of the status it shows. The background
    // chain first requests them once the first scene has loaded, while the
    // species and the status are known as soon as the state is. This moves
    // them ahead of view creation and the first scene, and requests their
    // spritesheets alongside the JSON instead of a round trip behind it.
    name: 'petAnimations',
    phase: 'replicantReady',
    assets: () => {
      // The FTUE loads the first puzzle instead. Without a species there is
      // still the crate, whose assets the tutorial that opens it loads.
      if (isLoadingFTUEActive()) return undefined;

      const state = user();
      const species = Pet.getters.getEquippedPetSpecies(state);
      if (!species) return undefined;

      const assets = getAnimationAssets(petAnimations[species].url);
      const status = getStatusAnimationName(getDisplayedPetState(state));
      if (status) {
        const locale = StateObserver.getLocale() === 'ja' ? 'ja' : 'en';
        assets.push(
          ...getAnimationAssets(getStatusAnimationPath(status, locale)),
        );
      }

      return new AssetGroup(assets);
    },
  },
  {
    // A level-1 player goes from the splash straight into the first puzzle,
    // and the launch sequence dismisses the splash only once its board is on
    // screen, yet `startMatch2` first asks for the puzzle's assets after
    // EntryFinal. Whether the FTUE is active is known as soon as the state
    // is.
    name: 'firstPuzzle',
    phase: 'replicantReady',
    assets: () => {
      if (!isLoadingFTUEActive()) return undefined;
      if (process.env.IS_EDITOR) return undefined;

      return getFirstPuzzleWarmUpAssets();
    },
  },
  {
    // Over a megabyte and decoded on arrival, so it is kept out of the
    // puzzle's asset group, where it stalled everything chained behind that
    // group. Nothing awaits it: `AudioManager` loads background music on
    // demand with an infinite `maxLoadDelay` and plays it once it is ready,
    // and `loader` dedupes requests in flight, so a play mid-download joins
    // this request. It waits for the pet's assets because starting it any
    // earlier took bandwidth from the pet's spritesheets under a 1 Mbps
    // profile, and a late start only delays the music.
    name: 'match2Music',
    phase: 'petLoaded',
    assets: () => new AssetGroup(['assets/sounds/music/Match2_BGM.mp3']),
  },
];
